import { isWebCompanionPlatform } from '@/utils/platform';
import { supabase } from '@/integrations/supabase/client';
import { getAllManuscripts as getLocalManuscripts } from '@/lib/db';
import { gatherBackupPayload } from '@/lib/backupPayload';
import { logger } from '@/lib/logger';
import { encrypt as cryptoEncrypt, generateSalt, deriveKey, APP_ENCRYPTION_SALT } from '@/lib/crypto';
import { getE2ESetupKey } from '@/utils/storageKeys';

const MIGRATED_KEY = 'birch_web_legacy_migrated';
// Deprecation target: Legacy Web Companion IndexedDB migration can be safely sunset after Q4 2026.

export async function checkAndMigrateWebLegacyData(): Promise<void> {
  if (!isWebCompanionPlatform()) return;

  const migrationRecord = localStorage.getItem(MIGRATED_KEY);
  if (migrationRecord) {
    return;
  }

  try {
    const { data: { session } } = await supabase.auth.getSession();
    const userId = session?.user?.id;
    if (!userId) return;

    // Check if browser has local IndexedDB manuscripts
    const localManuscripts = await getLocalManuscripts().catch(() => []);
    if (!localManuscripts || localManuscripts.length === 0) {
      localStorage.setItem(
        MIGRATED_KEY,
        JSON.stringify({ completedAt: new Date().toISOString(), count: 0, version: '1.0' })
      );
      return;
    }

    logger.log(`[WebLegacyMigrator] Found ${localManuscripts.length} local manuscripts in browser. Uploading to cloud...`);

    const isE2E = localStorage.getItem(getE2ESetupKey(userId)) === "true";

    for (const m of localManuscripts) {
      try {
        const payload = await gatherBackupPayload(m.id);
        const plaintext = JSON.stringify(payload);

        let contentStr = plaintext;
        let isEncrypted = false;

        if (isE2E) {
          try {
            const cryptoKey = await deriveKey(userId, APP_ENCRYPTION_SALT);
            const encryptedBlob = await cryptoEncrypt(plaintext, cryptoKey, generateSalt());
            contentStr = JSON.stringify(encryptedBlob);
            isEncrypted = true;
          } catch (encErr) {
            console.error(`[WebLegacyMigrator] E2E encryption failed for ${m.id}:`, encErr);
          }
        }

        await supabase.from('manuscript_backups').upsert({
          user_id: userId,
          manuscript_id: m.id,
          content: contentStr,
          is_encrypted: isEncrypted,
          updated_at: new Date().toISOString(),
        }, { onConflict: 'user_id,manuscript_id' });

        logger.log(`[WebLegacyMigrator] Uploaded "${m.title}" (${m.id}) to cloud.`);
      } catch (uploadErr) {
        console.error(`[WebLegacyMigrator] Failed to upload ${m.id}:`, uploadErr);
      }
    }

    localStorage.setItem(
      MIGRATED_KEY,
      JSON.stringify({ completedAt: new Date().toISOString(), count: localManuscripts.length, version: '1.0' })
    );
    logger.log('[WebLegacyMigrator] Legacy web data migration completed.');
  } catch (err) {
    console.error('[WebLegacyMigrator] Error during migration check:', err);
  }
}
